import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import {
  CollaborationAttachmentsDataService,
  CollaborationsGuardedDataService,
  Message,
  MessageContentAttachmentMediaTypeEnum,
} from '@ffd/api-client';
import { collaborationsActions, selectUnreadPersonalAssistantCollaborationIds } from '@libs/collaboration';
import { NurturingStore } from '@libs/nurturing';
import { NurturingFeature, selectCurrentUserProfile, selectPersonalAssistantCollabId } from '@libs/store';
import { AssistantBannerService } from './assistant-banner.service';

export interface AssistantMessagePreview {
  senderFirstName: string;
  senderLastName?: string;
  text: string;
  video?: AssistantMessagePreviewVideo;
}

export interface AssistantMessagePreviewVideo {
  messageId: string;
  attachmentId: string;
  // Missing until the thumbnail is fetched - and the backend only generates it some time after the
  // message arrives, which the ThumbnailReady websocket event signals.
  thumbnailUrl?: string;
}

// Cached lazily rather than typed against the generated model directly, so this service only depends
// on the fields it actually reads from Collaboration.participants.
interface CachedParticipants {
  employees: Array<{ userId?: string; firstName: string; lastName: string }>;
  patients?: Array<{ userId?: string; firstName: string; lastName: string }>;
}

@Injectable({ providedIn: 'root' })
export class AssistantLauncherService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly collaborationsGuardedDataService = inject(CollaborationsGuardedDataService);
  private readonly collaborationAttachmentsDataService = inject(CollaborationAttachmentsDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly nurturingStore = inject(NurturingStore);
  private readonly assistantBannerService = inject(AssistantBannerService);

  private readonly profile = this.store.selectSignal(selectCurrentUserProfile);
  private readonly siteCollabId = this.store.selectSignal(selectPersonalAssistantCollabId);
  private readonly unreadCollabIds = this.store.selectSignal(selectUnreadPersonalAssistantCollaborationIds);

  // FFD Notes uses the user's own assistant collaboration from the profile instead of the selected site's.
  public readonly isFfdNotes = signal(false);
  public readonly collabId = computed(() => (this.isFfdNotes() ? this.profile()?.ffdNotesPersonalAssistantCollabId : this.siteCollabId()));

  public readonly hasUnreadMessage = computed(() => {
    const collabId = this.collabId();
    return !!collabId && this.unreadCollabIds().includes(collabId);
  });
  public readonly latestPreview = signal<AssistantMessagePreview | null>(null);

  private participants: CachedParticipants | null = null;
  private initialized = false;
  // eslint-disable-next-line unicorn/no-useless-undefined
  private previewedCollabId: string | undefined = undefined;

  // The preview and the cached participants are scoped to one site's assistant collaboration, so a
  // site change has to drop both - otherwise the previous site's preview stays on screen and its
  // participants get reused to resolve the next sender's name.
  private readonly resetOnCollabChangeEffect = effect(() => {
    const collabId = this.collabId();
    if (collabId === this.previewedCollabId) {
      return;
    }
    this.previewedCollabId = collabId;
    this.participants = null;
    this.hidePreview();
  });

  // Both the main and the FFD Notes layout call this, and root subscriptions must not stack up.
  public init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.listenForAssistantMessages();
    this.listenForThumbnailReady();
    // Loaded before the chat opens, so the welcome card is ready when the user opens it
    this.assistantBannerService.init();
  }

  public showPreview(preview: AssistantMessagePreview): void {
    this.latestPreview.set(preview);
    if (preview.video && !preview.video.thumbnailUrl) {
      this.loadVideoThumbnail(preview.video);
    }
  }

  public hidePreview(): void {
    this.latestPreview.set(null);
  }

  // Opening the chat is the user actually engaging with what the nudge was pointing at, so it
  // permanently dismisses the feature - same pattern as e.g. starting autofill dismisses AutofillFirstTime.
  public dismissNudge(): void {
    this.nurturingStore.dismissImmediately(NurturingFeature.PersonalAssistantNudge);
  }

  private listenForAssistantMessages(): void {
    this.actions$
      .pipe(
        ofType(collaborationsActions.wsReceiveMessage),
        filter(({ data }) => {
          const collabId = this.collabId();
          return !!collabId && data.collaborationId === collabId;
        }),
        filter(
          ({ data }) =>
            !data.message.isSystemMessage &&
            (!!data.message.text || !!this.findVideo(data.message)) &&
            data.message.senderId !== this.profile()?.id,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ data }) => {
        this.resolvePreview(data.message);
      });
  }

  private listenForThumbnailReady(): void {
    this.actions$
      .pipe(
        ofType(collaborationsActions.wsThumbnailReady),
        filter(({ data }) => {
          const video = this.latestPreview()?.video;
          return !!video && !video.thumbnailUrl && video.messageId === data.messageId && video.attachmentId === data.attachmentId;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ data }) => {
        this.loadVideoThumbnail(data);
      });
  }

  private resolvePreview(message: Message): void {
    const collabId = this.collabId();

    if (!collabId) {
      return;
    }

    const text = message.text ?? '';
    const video = this.findVideo(message);

    if (this.participants) {
      this.setPreview(message.senderId, text, video);
      return;
    }

    this.collaborationsGuardedDataService
      .getCollaborationV2(collabId)
      .pipe(
        // The site can change while this is in flight - a late response must not resurface a preview
        // belonging to the site the user just left.
        filter(() => this.collabId() === collabId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (collaboration) => {
          this.participants = collaboration.participants;
          this.setPreview(message.senderId, text, video);
        },
        error: (error) => {
          console.error(error);
          if (this.collabId() !== collabId) {
            return;
          }
          // Sender name couldn't be resolved - still surface the message on its own.
          this.showPreview({ senderFirstName: '', text, video });
        },
      });
  }

  private setPreview(senderId: string, text: string, video: AssistantMessagePreviewVideo | undefined): void {
    const sender =
      this.participants?.employees.find((employee) => employee.userId === senderId) ??
      this.participants?.patients?.find((patient) => patient.userId === senderId);
    this.showPreview({ senderFirstName: sender?.firstName ?? '', senderLastName: sender?.lastName, text, video });
  }

  // Only the first video is previewed - images are not shown in the preview.
  private findVideo(message: Message): AssistantMessagePreviewVideo | undefined {
    const attachment = message.attachments?.find(({ mediaType }) => mediaType === MessageContentAttachmentMediaTypeEnum.RECORDING);
    return attachment ? { messageId: message.id, attachmentId: attachment.id } : undefined;
  }

  private loadVideoThumbnail({ messageId, attachmentId }: Pick<AssistantMessagePreviewVideo, 'messageId' | 'attachmentId'>): void {
    this.collaborationAttachmentsDataService
      .getThumbnailDownloadLinkForAttachment(attachmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ url }) => {
          // The preview can be replaced or dismissed while this is in flight.
          const preview = this.latestPreview();
          if (!url || preview?.video?.messageId !== messageId || preview.video.attachmentId !== attachmentId) {
            return;
          }
          this.latestPreview.set({ ...preview, video: { ...preview.video, thumbnailUrl: url } });
        },
        // Expected while the backend still generates the thumbnail: the placeholder stays until ThumbnailReady.
        error: (error) => console.warn(error),
      });
  }
}
